// components/leads/LeadFollowupPanel.js
// Follow-up Commerciale V3 (§21) — pannello Lead Detail: ultimo contatto, ultima
// lavorazione, prossimo passo, stato operativo + CTA imposta/modifica/completa.
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { leadApi } from '../../lib/api';
import { Compass, Clock, FileText, CheckCircle2, CalendarClock, AlertTriangle } from 'lucide-react';

const STATE_META = {
  new_assignment: { label: 'Nuovo assegnato', cls: 'bg-[#E5F4FF] text-[#1D6FD1]' },
  under_control: { label: 'Sotto controllo', cls: 'bg-[#EAF7EE] text-[#1E9E5A]' },
  waiting_planned: { label: 'Attesa pianificata', cls: 'bg-[#FFF4E0] text-[#C77700]' },
  needs_attention: { label: 'Da seguire', cls: 'bg-[#FDECEC] text-[#C0392B]' },
  next_step_overdue: { label: 'Passo scaduto', cls: 'bg-[#FDECEC] text-[#C0392B]' },
  terminal: { label: 'Chiuso', cls: 'bg-gray-100 text-gray-500' },
};

const NEXT_STEP_TYPES = [
  { v: 'ricontattare_cliente', l: 'Richiamare cliente' },
  { v: 'completare_revisione_preventivo', l: 'Completare revisione preventivo' },
  { v: 'fissare_appuntamento', l: 'Fissare appuntamento' },
  { v: 'presentare_preventivo', l: 'Presentare preventivo' },
  { v: 'attendere_cliente', l: 'Attesa cliente' },
  { v: 'altro', l: 'Altro' },
];

const WAITING_REASONS = [
  { v: 'none', l: 'Nessuno' },
  { v: 'decisione_cliente', l: 'Decisione cliente' },
  { v: 'finanziamento_mutuo', l: 'Finanziamento / mutuo' },
  { v: 'documenti_cliente', l: 'Documenti cliente' },
  { v: 'progetto_tecnico', l: 'Progetto tecnico' },
  { v: 'disponibilita_immobile', l: 'Disponibilità immobile' },
  { v: 'modifiche_preventivo', l: 'Modifiche preventivo' },
  { v: 'temporaneamente_irreperibile', l: 'Cliente irreperibile' },
  { v: 'altro', l: 'Altro' },
];

// Preset rapidi (§8) -> giorni da oggi. Europe/Rome lato client via Date locale.
const DATE_PRESETS = [
  { l: 'Tra 3 giorni', days: 3 },
  { l: '1 settimana', days: 7 },
  { l: '2 settimane', days: 14 },
  { l: '1 mese', days: 30 },
];

const fmtDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return null; }
};

const toDueIso = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  d.setHours(18, 0, 0, 0);
  return d.toISOString();
};

const Line = ({ icon: Icon, label, value, testId }) => (
  <div className="flex items-start gap-2" data-testid={testId}>
    <Icon className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
    <div className="min-w-0">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{label}</p>
      <p className="text-sm text-gray-800">{value || '—'}</p>
    </div>
  </div>
);

export const LeadFollowupPanel = ({ leadId, onChange }) => {
  const [fu, setFu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ type: '', waiting_reason: 'none', due_at: null, dueLabel: '', note: '' });

  const load = useCallback(async () => {
    try {
      const res = await leadApi.getFollowup(leadId);
      setFu(res.data);
    } catch { /* silent: panel optional */ } finally { setLoading(false); }
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  const openDialog = () => {
    setForm({
      type: fu?.next_step_type || '',
      waiting_reason: fu?.next_step_waiting_reason || 'none',
      due_at: fu?.next_step_due_at || null,
      dueLabel: fu?.next_step_due_at ? fmtDate(fu.next_step_due_at) : '',
      note: fu?.next_step_note || '',
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.type) { toast.error('Seleziona il tipo di prossimo passo'); return; }
    setSaving(true);
    try {
      const res = await leadApi.setNextStep(leadId, {
        type: form.type,
        waiting_reason: form.waiting_reason === 'none' ? null : form.waiting_reason,
        due_at: form.due_at,
        note: form.note || null,
        expected_version: fu?.next_step_version ?? null,
      });
      setFu(res.data);
      setOpen(false);
      toast.success('Prossimo passo aggiornato');
      if (onChange) onChange();
    } catch (e) {
      if (e?.response?.status === 409) { toast.error('Modificato da un altro utente. Aggiorno…'); setOpen(false); load(); }
      else toast.error('Errore nel salvataggio');
    } finally { setSaving(false); }
  };

  const complete = async () => {
    setSaving(true);
    try {
      const res = await leadApi.completeNextStep(leadId, { expected_version: fu?.next_step_version ?? null });
      setFu(res.data);
      toast.success('Prossimo passo completato');
      if (onChange) onChange();
    } catch (e) {
      if (e?.response?.status === 409) { toast.error('Modificato da un altro utente. Aggiorno…'); load(); }
      else toast.error('Errore');
    } finally { setSaving(false); }
  };

  if (loading || !fu) return null;

  const meta = STATE_META[fu.followup_state] || STATE_META.under_control;
  const hasActiveStep = fu.next_step_type && !fu.next_step_completed;
  const contactVal = fu.last_customer_contact_at
    ? `${fmtDate(fu.last_customer_contact_at)} (${fu.days_since_customer_contact} gg fa)` : 'Nessun contatto registrato';
  const workVal = fu.last_commercial_work_at
    ? `${fmtDate(fu.last_commercial_work_at)} (${fu.days_since_commercial_work} gg fa)` : 'Nessuna lavorazione';
  let stepVal = 'Nessun prossimo passo';
  if (hasActiveStep) {
    stepVal = fu.next_step_label || 'Prossimo passo';
    if (fu.next_step_waiting_label) stepVal += ` · ${fu.next_step_waiting_label}`;
    if (fu.next_step_due_at) stepVal += ` · ${fu.next_step_overdue ? 'scaduto ' : 'entro '}${fmtDate(fu.next_step_due_at)}`;
  }

  return (
    <Card className="bg-white rounded-xl border border-[#E4E4E4] shadow-sm" data-testid="lead-followup-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Compass className="w-5 h-5 text-[#FF7819]" />
            Follow-up commerciale
          </span>
          <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${meta.cls}`} data-testid="followup-state-badge">
            {fu.followup_state === 'next_step_overdue' && <AlertTriangle className="w-3 h-3 inline mr-1 -mt-0.5" />}
            {meta.label}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        <Line icon={Clock} label="Ultimo contatto cliente" value={contactVal} testId="followup-last-contact" />
        <Line icon={FileText} label="Ultima lavorazione" value={workVal} testId="followup-last-work" />
        <Line icon={CalendarClock} label="Prossimo passo" value={stepVal} testId="followup-next-step" />

        <div className="flex items-center gap-2 pt-1">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="flex-1" onClick={openDialog} data-testid="followup-set-next-step-btn">
                {hasActiveStep ? 'Modifica prossimo passo' : 'Imposta prossimo passo'}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md" data-testid="next-step-dialog">
              <DialogHeader>
                <DialogTitle>Prossimo passo</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 py-1">
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Tipo</label>
                  <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                    <SelectTrigger className="mt-1" data-testid="next-step-type"><SelectValue placeholder="Scegli tipo" /></SelectTrigger>
                    <SelectContent>
                      {NEXT_STEP_TYPES.map((t) => <SelectItem key={t.v} value={t.v} data-testid={`next-step-type-${t.v}`}>{t.l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Scadenza</label>
                  <div className="flex flex-wrap gap-1.5 mt-1" data-testid="next-step-presets">
                    {DATE_PRESETS.map((p) => (
                      <button key={p.days} type="button"
                        onClick={() => setForm((f) => ({ ...f, due_at: toDueIso(p.days), dueLabel: p.l }))}
                        data-testid={`next-step-preset-${p.days}`}
                        className={`px-2.5 py-1 text-xs rounded-full border ${
                          form.dueLabel === p.l ? 'bg-[#FF7819] text-white border-[#FF7819]' : 'bg-white text-[#525252] border-slate-300 hover:border-[#FF7819]'
                        }`}>{p.l}</button>
                    ))}
                    <input type="date" data-testid="next-step-date-picker"
                      onChange={(e) => {
                        if (!e.target.value) return;
                        const [y, m, dd] = e.target.value.split('-').map(Number);
                        const d = new Date(y, m - 1, dd, 18, 0, 0, 0);
                        setForm((f) => ({ ...f, due_at: d.toISOString(), dueLabel: fmtDate(d.toISOString()) }));
                      }}
                      className="px-2 py-1 text-xs rounded-md border border-slate-300" />
                  </div>
                  {form.due_at && <p className="text-xs text-gray-500 mt-1" data-testid="next-step-due-preview">Scadenza: {fmtDate(form.due_at)}</p>}
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Motivo attesa (opzionale)</label>
                  <Select value={form.waiting_reason} onValueChange={(v) => setForm((f) => ({ ...f, waiting_reason: v }))}>
                    <SelectTrigger className="mt-1" data-testid="next-step-waiting-reason"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {WAITING_REASONS.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Nota (opzionale)</label>
                  <Textarea value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                    placeholder="Dettagli del prossimo passo" className="mt-1" rows={2} data-testid="next-step-note" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} data-testid="next-step-cancel">Annulla</Button>
                <Button onClick={save} disabled={saving} className="bg-[#FF7819] hover:bg-[#e06a15] text-white" data-testid="next-step-save">
                  {saving ? 'Salvataggio...' : 'Salva'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          {hasActiveStep && (
            <Button variant="outline" size="sm" onClick={complete} disabled={saving}
              className="text-[#1E9E5A] border-[#1E9E5A]/40 hover:bg-[#EAF7EE]" data-testid="followup-complete-step-btn">
              <CheckCircle2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default LeadFollowupPanel;
